import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { z } from 'zod';
import { addValidationSourceAction } from './AddValidationSourceAction';
import { automationBlockerAction } from './AutomationBlockerAction';
import { customActionPayloadSchema } from './CustomAction';
import { documentStateActions } from './DocumentStateAction';
import { emailAction } from './EmailAction';
import { getDefaultEvent, implementedEvents } from './events';
import { excludeFromAiTrainingAction } from './ExcludeFromAiTrainingAction';
import { labelActions } from './LabelActions';
import { learnMemoryAction } from './LearnMemoryAction';
import { messageActions } from './MessageAction';
import { moveDocumentAction } from './MoveDocumentAction';
import { visibilityActions } from './VisibilityAction';

// Action order matters here
export const knownActions = [
  ...messageActions,
  emailAction,
  automationBlockerAction,
  addValidationSourceAction,
  excludeFromAiTrainingAction,
  moveDocumentAction,
  ...visibilityActions,
  ...labelActions,
  ...documentStateActions,
  learnMemoryAction,
] as const;

export const implementedActions = [
  'show_message',
  'add_automation_blocker',
  'change_queue',
  'hide_field',
  'send_email',
  'show_field',
  'show_hide_field',
  'add_validation_source',
  'exclude_from_training',
  'change_status',
  'add_label',
  'remove_label',
  'add_remove_label',
  'learn_memory',
] as const;

export const visibleActions = R.filter(knownActions, action =>
  R.isIncludedIn(action.serialized.type, implementedActions)
);

export const knownActionGroups = knownActions.map(
  action => action.groupIntlKey
);

const actionSchemaOptions = (intl?: IntlShape) => [
  ...knownActions.map(action => {
    const payloadSchema = action.payloadSchema(intl);
    const defaultEvent = getDefaultEvent(action);

    return z.object({
      type: z.literal(action.serialized.type),
      event: z.enum(implementedEvents).optional().default(defaultEvent),
      payload: payloadSchema,
    });
  }),
  // Ugly but quick hack to add a "generic" custom action instead of using the constructor
  z.object({
    type: z.literal('custom'),
    event: z.enum(implementedEvents).optional().default('validation'),
    payload: customActionPayloadSchema(intl),
  }),
];

export const actionSchema = (intl?: IntlShape) => {
  const schemas = actionSchemaOptions(intl);
  if (schemas.length === 0) {
    throw new Error('There are no known actions to create a schema from.');
  }

  // We must prove to TypeScript that the array of schemas is not empty.
  // We do this by destructuring it into a [first, ...rest] tuple.
  const [firstActionSchema, secondActionSchema, ...restActionSchemas] = schemas;

  // Add a runtime check for safety, in case `knownActions` is ever empty.
  if (!firstActionSchema || !secondActionSchema) {
    throw new Error('There are no known actions to create a schema from.');
  }

  const unionSchema = z.union([
    firstActionSchema,
    secondActionSchema,
    ...restActionSchemas,
  ]);

  return z
    .object({
      type: z.string().min(
        1,
        intl?.formatMessage({
          id: 'features.rules.errors.actionNotSelected',
        })
      ),
      event: z.string().optional(),
      payload: z.any(),
    })
    .pipe(unionSchema);
};
